import { useSelector, useDispatch } from "react-redux";
import { setLangAsync } from "./i18nSlice";

export default function useTranslations() {
  const dispatch = useDispatch();

  const translations = useSelector((state) => state.i18n.translations);
  const lang = useSelector((state) => state.i18n.lang);
  const supportedLangs = useSelector((state) => state.i18n.supportedLangs);
  const status = useSelector((state) => state.i18n.status);

  // Enhanced translation function with nested key support
  const t = (key, params = {}) => {
    if (!key || !translations) return key;

    // Support nested keys like "profile.title" or "buttons.save"
    const keys = key.split(".");
    let value = translations;

    for (const nestedKey of keys) {
      if (value && typeof value === "object" && nestedKey in value) {
        value = value[nestedKey];
      } else {
        console.warn(`Translation key "${key}" not found for language "${lang}"`);
        return key; // Return key if translation not found
      }
    }

    // Support parameter interpolation
    if (typeof value === "string" && Object.keys(params).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return params[paramKey] || match;
      });
    }

    return value;
  };

  const setLang = (language) => {
    try {
      localStorage.setItem("lang", language);
    } catch (e) {
      console.error("Failed to save language to localStorage:", e);
    }
    return dispatch(setLangAsync(language));
  };

  // Get current language display name
  const getCurrentLanguageName = () => {
    return supportedLangs[lang] || lang;
  };

  // Check if a specific language is supported
  const isLanguageSupported = (language) => {
    return Object.keys(supportedLangs).includes(language);
  };

  // Get available languages as array
  const getAvailableLanguages = () => {
    return Object.entries(supportedLangs).map(([code, name]) => ({
      code,
      name,
      isActive: code === lang,
    }));
  };

  return {
    t,
    lang,
    setLang,
    init: setLang,
    supportedLangs,
    status,
    getCurrentLanguageName,
    isLanguageSupported,
    getAvailableLanguages,
    isLoading: status === "loading",
  };
}
